import React, { useEffect } from "react";
import { motion } from 'framer-motion';
import { BookOpen, Lightbulb, Globe, Users, Award, MapPin, Monitor, Book, Crown } from 'lucide-react';
import logo from '/img/logo.png';

const features = [
  {
    icon: <BookOpen size={22} />,
    title: 'Skill-focused Curriculum',
    text: 'We equip graduates with the necessary skills and knowledge needed in today’s challenging economy.'
  },
  {
    icon: <Lightbulb size={22} />,
    title: 'Curiosity & Creativity',
    text: 'We capitalise on curiosity, creativity, and confidence to shape future-ready professionals.'
  },
  {
    icon: <Globe size={22} />,
    title: 'Global Faculty',
    text: 'Our faculty includes internationally experienced scholars with degrees from the USA, UK, India and beyond.'
  },
  {
    icon: <Monitor size={22} />,
    title: 'Applied & Job-oriented Teaching',
    text: 'Applied teaching and job-oriented training programmes are integral to the curriculum.'
  },
  {
    icon: <Award size={22} />,
    title: 'Ethics & Accountability',
    text: 'We uphold the highest ethical standards and are accountable for our actions.'
  },
  {
    icon: <Users size={22} />,
    title: 'Inclusive Community',
    text: 'We welcome diverse perspectives and build a creative learning environment that values everyone.'
  },
  {
    icon: <MapPin size={22} />,
    title: 'Vibrant Location',
    text: 'Located in a lively area with shops, restaurants, theatres, and cultural venues for students.'
  },
  {
    icon: <Crown size={22} />,
    title: 'Clubs & Extra-curricular',
    text: 'Multiple clubs (Business, Sports, Debate, Creative) nurture students’ latent talent.'
  },
  {
    icon: <Book size={22} />,
    title: 'Facilities & Smart Classrooms',
    text: 'Smartboards, projectors, and modern facilities make learning effective and engaging.'
  },
  {
    icon: <Globe size={22} />,
    title: 'Scholarships & Support',
    text: 'We offer various scholarships to support talented and financially disadvantaged students.'
  },
  {
    icon: <Users size={22} />,
    title: 'Friendly Campus',
    text: 'A welcoming and friendly environment where students and staff communicate comfortably.'
  },
  {
    icon: <Award size={22} />,
    title: 'Alumni Success',
    text: 'Our graduates are making notable professional impact both locally and abroad.'
  }
];

export default function WhyStudy({ 
  accent = "#115e32",
  pageTitle = "Why Study at UCTC? | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  return (
    <section className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] py-16 px-4 md:px-12">
      {/* Hero */}
      <div className="max-w-6xl mx-auto bg-white/70 rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid md:grid-cols-2 gap-6 items-center p-8 md:p-12 bg-gradient-to-r from-[#6BCCF2] to-teal-50">
          <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="z-10">
            <img src={logo} alt="UCTC" className="w-28 mb-4" />
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#014864] leading-tight">Why Study at UCTC?</h1>
            <p className="mt-4 text-gray-700 max-w-lg">A new movement in higher education began in the 1990s. UCTC exists to produce technologically capable, ethical, and creative professionals who can meet industry demand and drive socio-economic progress.</p>

            <div className="mt-6 flex gap-3">
              <a href="#apply" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#014864] to-[#9EE1FA] text-white font-semibold shadow hover:scale-105 transition">Apply Now</a>
              <a href="#programs" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#f1e0b0] text-[#7c5b0e] bg-white/60 hover:scale-105 transition">Our Programs</a>
            </div>
          </motion.div>

          {/* Hero SVG with floating icons */}
          <motion.div initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.9 }} className="relative">
            <svg viewBox="0 0 520 320" className="w-full h-full">
              <defs>
                <linearGradient id="gGold" x1="0" x2="1">
                  <stop offset="0%" stopColor="#fff3d1" />
                  <stop offset="100%" stopColor="#ffe9b0" />
                </linearGradient>
              </defs>

              <g>
                <motion.ellipse cx="160" cy="160" rx="140" ry="80" fill="url(#gGold)" opacity="0.95" animate={{ ry: [80, 76, 80] }} transition={{ duration: 6, repeat: Infinity }} />
                <motion.rect x="280" y="40" rx="12" width="200" height="200" fill="#fff" stroke="#f4e2b0" animate={{ y: [0, -6, 0] }} transition={{ duration: 5, repeat: Infinity }} />

                {/* floating icons */}
                <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                  <motion.g animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                    <rect x="40" y="30" width="72" height="56" rx="10" fill="#fff" stroke="#f3e0b1" />
                    <text x="76" y="64" textAnchor="middle" fontSize="18" fill="#b88f18" fontWeight="700">📘</text>
                  </motion.g>

                  <motion.g animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity }}>
                    <circle cx="420" cy="240" r="24" fill="#fff" stroke="#f3e0b1" />
                    <text x="420" y="247" textAnchor="middle" fontSize="18">🌐</text>
                  </motion.g>

                  <motion.g animate={{ y: [0, -6, 0] }} transition={{ duration: 3.6, repeat: Infinity }}>
                    <rect x="120" y="180" width="96" height="48" rx="8" fill="#fff" stroke="#f3e0b1" />
                    <text x="168" y="210" textAnchor="middle" fontSize="18">💡</text>
                  </motion.g>
                </motion.g>
              </g>
            </svg>
          </motion.div>
        </div>

        {/* Body */}
        <div className="p-8 md:p-12 bg-[#B7EAFC]">
          <motion.div className="max-w-6xl mx-auto">
            <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-gray-700 mb-6">
              The prospective students should choose UCTC for the following reasons:
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <motion.article key={i} whileHover={{ scale: 1.03 }} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06 }} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-lg border-t-4 border-[#f3e0b1]">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#fff2d0] to-[#ffe7b0] text-[#7c5b0e]">
                      {f.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{f.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{f.text}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* Extra info area */}
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-8 bg-white rounded-2xl p-6 shadow-md border-t-4 border-[#e8c57a]">
              <h3 className="text-lg font-bold text-[#014864] mb-3">Campus Life & Future Plans</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>On campus, a vibrant and friendly environment prevails making communication with staff and peers easy.</li>
                <li>We are looking forward to shifting to a permanent campus soon and commencing transport facilities for students and faculty.</li>
                <li>Students enjoy a variety of clubs and extracurriculars that help build leadership and soft skills.</li>
                <li>UCTC provides academic & technological facilities to enhance the student experience.</li>
              </ul>
            </motion.div>

            <div className="mt-8 flex flex-col md:flex-row gap-4 items-center justify-between">
              <motion.div initial={{ x: -10, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-gray-700 max-w-xl">
                <h4 className="text-lg font-bold text-[#014864]">Scholarships & Support</h4>
                <p className="mt-2 text-sm">We offer scholarships for exceptionally talented students as well as need-based support for those who require financial assistance.</p>
              </motion.div>

              <motion.div initial={{ x: 10, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <a href="#apply" className="px-5 py-3 rounded-lg bg-[#014864] text-white font-semibold shadow hover:brightness-105 transition">Apply Now</a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
